import React, { useState, useEffect } from 'react'
import styles from './MyProfile.module.css'
import ProfileImageModal from './ProfileImageModal'
import PasswordChangeModal from './PasswordChangeModal'
import InterestsModal from './InterestsModal'
import Header from '../Layout/Header'
import userIcon from '../../assets/icons/profileIconBig.svg'
import personIcon from '../../assets/icons/person.svg'
import IDcardIcon from '../../assets/icons/IDcard.svg'
import axios from 'axios'
import { getAccessToken } from '../Auth/AuthStore'
import { AlertModal } from '../AlertModal'



const MyProfile = () => {
  const [profileData, setProfileData] = useState({
    name: '',
    nickname: '',
    email: '',
    birthDate: '',
    gender: '',
    password: 'examplePss',
    interests: [],
    hobbies: [],
    profileImage: null
  })

  const [interestsText, setInterestsText] = useState('')

  const [showImageModal, setShowImageModal] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [showInterestsModal, setShowInterestsModal] = useState(false)
  const [showPasswords, setShowPasswords] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')

  const interestLabels = {
    MUSIC: '음악',
    NATURE_ANIMAL: '자연 / 동물',
    GAME: '게임',
    FITNESS: '운동 / 피트니스',
    BOOK_WRITING: '책 / 글쓰기',
    MOVIE_DRAMA: '영화 / 드라마',
    TRAVEL: '여행',
    FASHION_STYLE: '패션 / 스타일',
    FOOD_COOKING: '음식 / 요리',
    ART_DESIGN: '미술 / 디자인'
  }

  /* api 연동 */
  const fetchMyPage = async () => {
    const token = getAccessToken()
    if (!token) {
      throw new Error('로그인 토큰이 없습니다.')
    }

    try {
      const response = await axios.get(`${import.meta.env.VITE_API_BASE}/api/user/mypage`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      const data = response.data.data

      const hobbies = data.hobbies || []

      setProfileData(prev => ({
        ...prev,
        name: data.username,
        nickname: data.nickname,
        email: data.email,
        birthDate: data.birthDate,
        gender: data.gender,
        profileImage: data.profileImageUrl,
        hobbies,
      }))

      // hobbies 코드를 한국어로 변환
      const interestText = hobbies
        .map(hobby => interestLabels[hobby])
        .filter(Boolean)
        .join(', ')
      
      setInterestsText(interestText)

    } catch (error) {
      console.error(error)
      setAlertMessage('마이페이지 조회 실패')
      setShowAlert(true)
    }
  }

  useEffect(() => {
    fetchMyPage()
  }, [])

  const togglePasswordVisibility = () => {
    setShowPasswords(prev => !prev)
  }

  const handleSaveProfileImage = async (file) => {
    try {
      if (!file) {
        return
      }

      const token = getAccessToken()
      if (!token) {
        throw new Error('로그인 토큰이 없습니다.')
      }

      const mimeType = file.type || 'image/png'

      const response = await axios.post(`${import.meta.env.VITE_API_BASE}/api/s3/presigned-url`,
        { mimeType },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      const presignedUrl = response.data?.data?.presignedUrl
      if (!presignedUrl) throw new Error('presignedUrl이 응답에 없습니다.')

      const imageUrl = presignedUrl.split('?')[0]

      await axios.put(presignedUrl, file, {
        headers: { 'Content-Type': mimeType }
      })

      const patchRes = await axios.patch(
        `${import.meta.env.VITE_API_BASE}/api/user/mypage/profileImage`,
        { profileImageUrl: imageUrl }, 
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )

      const newProfileImage =
        patchRes.data?.data?.profileImageUrl || imageUrl

      setProfileData(prev => ({
        ...prev,
        profileImage: newProfileImage
      }))

      setAlertMessage('프로필 이미지가 변경되었습니다.')
      setShowAlert(true)

    } catch (error) {
      console.error('프로필 이미지 변경 실패:', error)
      setAlertMessage('프로필 이미지 변경에 실패했습니다.')
      setShowAlert(true)
    }
  }

  const handleSavePassword = async ({ currentPassword, newPassword }) => {
    try {
      const token = getAccessToken()
      if (!token) {
        throw new Error('로그인 토큰이 없습니다.')
      }
  
      const res = await axios.patch(
        `${import.meta.env.VITE_API_BASE}/api/user/mypage/password`,
        {
          currentPassword,
          newPassword,
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )

      const message = res.data?.message || '비밀번호가 변경되었습니다.'
      setAlertMessage(message)
      setShowAlert(true)
  
      setProfileData(prev => ({
        ...prev,
        password: '********', 
      }))
  
    } catch (error) {
      console.error('비밀번호 변경 실패:', error)
      const msg =
        error.response?.data?.message ||
        '비밀번호 변경에 실패했습니다. 현재 비밀번호를 다시 확인해주세요.'
      setAlertMessage(msg)
      setShowAlert(true)
    }
  }

  const handleSaveInterests = async (selectedInterests) => {
    try {
      const token = getAccessToken()
      if (!token) {
        throw new Error('로그인 토큰이 없습니다.')
      }
  
      const res = await axios.patch(
        `${import.meta.env.VITE_API_BASE}/api/user/mypage/hobbies`,
        { hobbies: selectedInterests }, 
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )
  
      const message = res.data?.message || '관심사가 변경되었습니다.'
      setAlertMessage(message)
      setShowAlert(true)
  
      const interestText = selectedInterests
        .map(id => interestLabels[id])
        .filter(Boolean)
        .join(', ')
  
      setProfileData(prev => ({
        ...prev,
        hobbies: selectedInterests,
        interests: selectedInterests,
      }))
  
      setInterestsText(interestText)
  
    } catch (error) {
      console.error('관심사 변경 실패:', error)
      const msg =
        error.response?.data?.message ||
        '관심사 변경에 실패했습니다. 잠시 후 다시 시도해주세요.'
      setAlertMessage(msg)
      setShowAlert(true)
    }
  }

  return (
    <>
      <Header />
      <div className={styles.container}>
        <div className={styles.header}>
          <img src={personIcon} alt = "사람 모양 아이콘" draggable={false}/>
          <h1>내 정보</h1>
        </div>

      <div className={styles.profileCard}>
        <div className={styles.cardHeader}>
          <img src={IDcardIcon} alt = "id카드 아이콘" draggable={false}/>
          <h2>프로필 정보</h2>
        </div>

        <div className={styles.profileContent}>
          {/* 프로필 이미지 */}
          <div className={styles.avatarSection}>
            <div className={styles.profileIcon}>
              {profileData.profileImage ? (
                <img src={profileData.profileImage} alt="Profile" />
              ) : (
                <img src={userIcon} alt = "유저아이콘" draggable={false}/>
              )} 
            </div>
            <div className={styles.avatarInfo}>
              <h1>{profileData.nickname}</h1>
              <button
                className={styles.editButton}
                onClick={() => setShowImageModal(true)}
              >
                프로필 변경
              </button>
            </div>
          </div>

          {/* 정보 필드들 */}
          <div className={styles.formGrid}>
            {/* 이름 */}
            <div className={styles.formGroup}>
              <label>이름</label>
              <input 
                type="text" 
                value={profileData.name}
                className={`${styles.input} ${styles.disabled}`}
                disabled
              />
            </div>

            {/* 이메일 */}
            <div className={styles.formGroup}>
              <label>이메일</label>
              <input 
                type="email" 
                value={profileData.email}
                className={`${styles.input} ${styles.disabled}`}
                disabled
              />
            </div>

            {/* 생년월일/성별 */}
            <div className={styles.formGroup}>
              <label>생년월일 / 성별</label>
              <input 
                type="text" 
                value={`${profileData.birthDate} / ${profileData.gender}`}
                className={`${styles.input} ${styles.disabled}`}
                disabled
              />
            </div>

            {/* 비밀번호 */}
            <div className={styles.formGroup}>
              <label>
                비밀번호 
                <button 
                  className={styles.editLink}
                  onClick={() => setShowPasswordModal(true)}
                >
                  변경
                </button>
              </label>
              <div className={styles.passwordField}>
                <input 
                  type={showPasswords ? 'text' : 'password'}
                  value={profileData.password}
                  className={`${styles.input} ${styles.disabled}`}
                  disabled
                  readOnly
                />
                {/* <button 
                  className={styles.toggleBtn}
                  onClick={() => togglePasswordVisibility()}
                >
                  <i className={showPasswords ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                </button> */}
              </div>
            </div>

            {/* 관심사 */}
            <div className={styles.formGroupFull}>
              <label>
                관심사 
                <button
                  className={styles.editLink}
                  onClick={() => setShowInterestsModal(true)}
                  >
                  변경
                 
                </button>
              </label>
              <input 
                type="text" 
                value={interestsText || ''}
                className={`${styles.input} ${styles.disabled}`}
                placeholder="책 / 글쓰기, 음식 / 요리"
                disabled
                readOnly
              />
            </div>
          </div>
        </div>
      </div>

      <ProfileImageModal
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        onSave={handleSaveProfileImage}
        currentImage={profileData.profileImage}
      />

      <PasswordChangeModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onSave={handleSavePassword}
        currentPassword={profileData.password}
      />

      <InterestsModal
        isOpen={showInterestsModal}
        onClose={() => setShowInterestsModal(false)}
        onSave={handleSaveInterests}
        currentInterests={profileData.interests}
      />

      <AlertModal
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        message={alertMessage}
      />
      </div>
    </>
  )
}

export default MyProfile
